import { z } from 'zod';
import { formFieldSanitisedText } from './sanitised-text.js';
import { SARS_REFERENCE_SEPARATORS } from './tax-reference.js';
// =============================================================================
// VAT treatment: enum, rate map, extraction helper
// =============================================================================
/**
 * Per-account VAT treatment. Controls whether a direct bank-line allocation to
 * this GL account splits the VAT portion out to VAT Control.
 *
 *   STANDARD_RATED_SALES      – output VAT at the SA standard rate (15%)
 *   STANDARD_RATED_PURCHASES  – input VAT at the SA standard rate (15%)
 *   NO_VAT                    – explicitly marked no-VAT (gross-post, same as null)
 *
 * `null` on an account means legacy/unset; both `null` and `NO_VAT` result in a
 * single gross-post leg with no VAT split.
 */
export const VatTreatmentSchema = z.enum([
    'STANDARD_RATED_SALES',
    'STANDARD_RATED_PURCHASES',
    'NO_VAT',
]);
/**
 * Explicit VAT treatment a caller may supply on the purchase side (expense
 * approval, reallocation posture). The decision is binary - claim input VAT or
 * post gross - so `STANDARD_RATED_SALES` (an output treatment, incoherent on a
 * purchase) is rejected at the schema instead of being silently gross-posted.
 * Account *defaults* remain the full `VatTreatment` enum; only the explicit
 * caller input is narrowed.
 */
export const ExpenseVatTreatmentSchema = VatTreatmentSchema.extract([
    'STANDARD_RATED_PURCHASES',
    'NO_VAT',
]);
/**
 * South African standard VAT rate expressed in basis points (15 % = 1500 bp).
 * Promoted from `invoice.service.ts` so every posting surface references the
 * same constant.
 */
export const SA_VAT_RATE_BASIS_POINTS = 1500;
/**
 * Maps each `VatTreatment` to its applicable rate (basis points) and the
 * direction VAT flows. Single source of truth shared by the backend leg-builder
 * and the frontend inline indicator.
 */
export const VAT_TREATMENT_RATES = Object.freeze({
    STANDARD_RATED_SALES: { rateBasisPoints: SA_VAT_RATE_BASIS_POINTS, direction: 'output' },
    STANDARD_RATED_PURCHASES: { rateBasisPoints: SA_VAT_RATE_BASIS_POINTS, direction: 'input' },
    NO_VAT: { rateBasisPoints: 0, direction: 'none' },
});
/**
 * Whether a `VatTreatment` claims input VAT (i.e. is a purchase-side treatment
 * whose VAT portion the firm reclaims from SARS). Derived from
 * `VAT_TREATMENT_RATES[...].direction === 'input'` rather than a hardcoded
 * literal, so the rate map's `direction` stays the single source of truth for
 * "which treatments claim input VAT".
 *
 * The single site for the input-VAT gate, shared by the backend supplier-invoice
 * payable journal and the CLI expense-plan predicate so the two cannot drift
 * apart on a literal string comparison. Returns `false` for `null`/`undefined`
 * (no treatment resolved) and for any `direction !== 'input'` treatment
 * (`NO_VAT`, `STANDARD_RATED_SALES`).
 */
export function claimsInputVat(treatment) {
    if (!treatment)
        return false;
    return VAT_TREATMENT_RATES[treatment].direction === 'input';
}
/**
 * Whether a sales journal needs VAT Control. Captured VAT can be nonzero even
 * when its display rate rounds to zero; positive rates also require the account
 * for lines whose VAT is derived. The firm's registration does not decide this.
 */
export function chargesOutputVat(lines) {
    return lines.some((line) => line.vatRateBasisPoints > 0 || (line.vatAmountMinorUnits ?? 0) !== 0);
}
/**
 * Whether a treatment produces a VAT split at all, in either direction, i.e. it
 * carries a non-zero rate. Amount-independent, unlike `deriveVatSplit`, which
 * also returns null when the rate is real but the amount is too small to round
 * to a cent of VAT.
 *
 * This is the predicate for "is this account VAT-bearing?" - the question the
 * allocation surfaces ask to decide whether their per-allocation VAT toggle
 * starts on. Derived from the rate map rather than a literal enum comparison so
 * `VAT_TREATMENT_RATES` stays the single source of truth.
 */
export function isVatBearing(treatment) {
    if (!treatment)
        return false;
    const { direction, rateBasisPoints } = VAT_TREATMENT_RATES[treatment];
    return direction !== 'none' && rateBasisPoints !== 0;
}
/**
 * The standard-rated treatment implied by a signed allocation leg.
 *
 * Debit-positive convention: a positive leg debits the target account, so a VAT
 * split is input VAT. A negative leg credits the target account, so a VAT split
 * is output VAT.
 */
export function standardRatedTreatmentForLeg(legMinorUnits) {
    return legMinorUnits >= 0 ? 'STANDARD_RATED_PURCHASES' : 'STANDARD_RATED_SALES';
}
/**
 * Whether a per-allocation VAT toggle should start on for a target account.
 * Leaving the toggle untouched therefore preserves the account default.
 */
export function defaultVatInclusive(accountDefault) {
    return isVatBearing(accountDefault);
}
/**
 * Resolve the treatment a single allocation row should post under.
 *
 * Explicitly toggling VAT off posts gross (`NO_VAT`). Toggling VAT on preserves
 * a VAT-bearing account default, otherwise it falls back to the signed leg
 * direction.
 */
export function resolveAllocationVatTreatment(accountDefault, vatInclusive, legMinorUnits) {
    if (!vatInclusive)
        return 'NO_VAT';
    if (accountDefault && isVatBearing(accountDefault))
        return accountDefault;
    return standardRatedTreatmentForLeg(legMinorUnits);
}
/**
 * Whether a VAT split is structurally possible on these books.
 */
export function canSplitVat(isVatRegistered, vatControlAccountId) {
    return resolveVatControlAccountId(isVatRegistered, vatControlAccountId) !== null;
}
/**
 * Return the VAT Control account id exactly when a VAT split can be posted.
 */
export function resolveVatControlAccountId(isVatRegistered, vatControlAccountId) {
    return isVatRegistered ? vatControlAccountId : null;
}
/**
 * Extract VAT from a VAT-INCLUSIVE non-negative magnitude using the tax fraction.
 *
 *   net = amount − round(amount × rate / (10000 + rate))
 *   vat = amount − net
 *
 * For `rateBasisPoints = 0` returns `{ net: amountMinorUnits, vat: 0 }`.
 * Callers are responsible for re-applying the correct sign; this function
 * operates on magnitudes only.
 *
 * Precision: `amountMinorUnits × rateBasisPoints` must stay within
 * `Number.MAX_SAFE_INTEGER`, i.e. amounts below ~R60bn (in cents) per call.
 * Real bank lines are far below this; larger inputs would lose integer precision.
 *
 * @param amountMinorUnits  Non-negative VAT-inclusive amount in minor units.
 * @param rateBasisPoints   VAT rate in basis points (e.g. 1500 for 15%).
 */
export function extractInclusiveVat(amountMinorUnits, rateBasisPoints) {
    if (rateBasisPoints === 0) {
        return { net: amountMinorUnits, vat: 0 };
    }
    const vat = Math.round((amountMinorUnits * rateBasisPoints) / (10000 + rateBasisPoints));
    const net = amountMinorUnits - vat;
    return { net, vat };
}
/**
 * Split a SIGNED, VAT-inclusive allocation amount into its net and VAT portions
 * for a given account treatment, preserving the original sign. The two returned
 * amounts always sum back to `grossMinorUnits`, so a caller that maps `net` onto
 * the target-account leg and `vat` onto the VAT Control leg keeps the entry set
 * balanced.
 *
 * Returns `null` when no split applies - a `null`/`NO_VAT` treatment, a
 * zero-rate treatment, or an amount so small the VAT rounds to zero - in which
 * case the caller posts the gross amount as a single leg.
 *
 * This is the single source of truth for the gross -> (net, VAT) computation,
 * shared by the frontend allocation surfaces (the inline indicator, the
 * one-click chip, and the split editor) so the preview and the posted legs can
 * never disagree. It is intentionally account-agnostic: it returns amounts, and
 * the caller owns the registration / VAT-Control-account gating and the mapping
 * of those amounts onto ledger-account legs.
 *
 * @param grossMinorUnits  Signed VAT-inclusive amount in minor units.
 * @param treatment        The target account's VAT treatment, or null/undefined.
 */
export function deriveVatSplit(grossMinorUnits, treatment) {
    if (!treatment)
        return null;
    const rates = VAT_TREATMENT_RATES[treatment];
    if (rates.direction === 'none' || rates.rateBasisPoints === 0)
        return null;
    const sign = grossMinorUnits < 0 ? -1 : 1;
    const { net, vat } = extractInclusiveVat(Math.abs(grossMinorUnits), rates.rateBasisPoints);
    if (vat === 0)
        return null;
    return { net: sign * net, vat: sign * vat };
}
/**
 * Build the shared bank-statement split payload for one gross allocation row.
 */
export function buildAllocationSplitLegs(grossMinorUnits, treatment, opts) {
    const effectiveTreatment = treatment ?? 'NO_VAT';
    const resolvedVatControlAccountId = resolveVatControlAccountId(opts.isVatRegistered, opts.vatControlAccountId);
    const split = resolvedVatControlAccountId
        ? deriveVatSplit(grossMinorUnits, effectiveTreatment)
        : null;
    if (split && resolvedVatControlAccountId) {
        return [
            {
                ledgerAccountId: opts.ledgerAccountId,
                amountMinorUnits: split.net,
                vatTreatment: effectiveTreatment,
            },
            {
                ledgerAccountId: resolvedVatControlAccountId,
                amountMinorUnits: split.vat,
                vatTreatment: effectiveTreatment,
            },
        ];
    }
    return [{
            ledgerAccountId: opts.ledgerAccountId,
            amountMinorUnits: grossMinorUnits,
            vatTreatment: effectiveTreatment,
        }];
}
/**
 * Expand user-visible allocation rows into shared bank-statement split legs.
 */
export function expandVatAwareAllocationRowsToSplits(rows, opts) {
    const totals = new Map();
    const add = (leg) => {
        const existing = totals.get(leg.ledgerAccountId);
        if (!existing) {
            totals.set(leg.ledgerAccountId, { ...leg });
            return;
        }
        existing.amountMinorUnits += leg.amountMinorUnits;
        if (existing.vatTreatment !== leg.vatTreatment) {
            delete existing.vatTreatment;
        }
    };
    for (const row of rows) {
        const treatment = resolveAllocationVatTreatment(row.accountDefault, row.vatInclusive, row.signedMinorUnits);
        for (const leg of buildAllocationSplitLegs(row.signedMinorUnits, treatment, {
            ...opts,
            ledgerAccountId: row.ledgerAccountId,
        })) {
            add(leg);
        }
    }
    return [...totals.values()].filter((leg) => leg.amountMinorUnits !== 0);
}
/**
 * Effective VAT rate (in basis points) implied by an amount pair:
 * `round(vat / subtotal x 10000)`. Returns 0 when subtotal is 0 (no base to
 * imply a rate from).
 *
 * A single standard-rated invoice yields 1500; a mixed-rate invoice (some lines
 * exempt) yields a blended rate, e.g. subtotal R18,472.58 + VAT R2,764.89 gives
 * 1497. The single source of truth for the rate snapshot recorded on supplier
 * invoices (server-derived from the VAT amount, never sent by the caller) and
 * for the effective-rate hint shown on the expense form, so the stored snapshot
 * and the on-screen preview can never disagree. The extraction normaliser uses
 * the same helper to imply a rate from the amounts it reads off the invoice.
 *
 * @param subtotalMinorUnits  Net (pre-VAT) amount in minor units.
 * @param vatAmountMinorUnits VAT amount in minor units.
 */
export function effectiveVatRateBasisPoints(subtotalMinorUnits, vatAmountMinorUnits) {
    if (subtotalMinorUnits <= 0)
        return 0;
    return Math.round((vatAmountMinorUnits / subtotalMinorUnits) * 10000);
}
// =============================================================================
// VAT - schema, sanitiser, constants
// =============================================================================
/**
 * SARS VAT tax-period categories (VAT Act s27). 'A' and 'B' are the two-monthly
 * cycles: A ends on odd months (Jan, Mar, May, Jul, Sep, Nov), B on even months
 * (Feb, Apr, Jun, Aug, Oct, Dec). SARS ALLOCATES the category at registration and
 * notifies the vendor in writing – the vendor does not choose it. We capture the
 * allocated value; there is no canonical "default" between A and B. Categories
 * C (monthly), D/F (six-monthly), E (annual) exist but are out of scope here.
 */
export const VatFilingCategorySchema = z.enum(['A', 'B']);
/**
 * How a vendor accounts for VAT (VAT Act s15): on invoice or on payments.
 * ALL_CAPS to match `EntityType` / `VatFilingCategory` and the Prisma enum.
 */
export const VatBasisSchema = z.enum(['INVOICE', 'PAYMENTS']);
/**
 * Regex for a South African VAT registration number: 10 digits starting with 4.
 * Shared so the sanitiser, the wire schema, and any frontend form schema stay
 * in lockstep.
 */
export const SA_VAT_REGEX = /^4\d{9}$/;
/**
 * Whether a firm is VAT-registered, determined solely by its business entity's
 * `vatNumber`: registered when the number is a non-null, non-blank string.
 *
 * The single source of truth for the registration predicate, shared by the
 * backend posting surfaces (invoice and supplier-invoice journals) and the
 * frontend VAT-split surfaces, so the UI preview cannot drift from what the
 * service posts. Pure: depends only on the number, not on the BusinessEntity row.
 */
export function isVatRegistered(vatNumber) {
    return vatNumber != null && vatNumber.trim() !== '';
}
/**
 * Sanitise an SA VAT number to canonical form: 10 digits starting with 4.
 *
 * A VAT number is routinely typed or pasted off a printed document with
 * separators baked in - spaces ("4123 456 789"), slashes, hyphens, or dots -
 * so this strips the same named separator set `sanitiseSarsTaxReference`
 * does (`SARS_REFERENCE_SEPARATORS`), never every non-digit character: a
 * permissive strip would silently accept junk like "4123a56784" whenever the
 * letters happened to leave 10 digits behind. Returns the canonical 10-digit
 * string, or `null` if the input cannot be coerced into a valid SA VAT
 * number.
 */
export function sanitiseVatNumber(input) {
    const stripped = input.replace(SARS_REFERENCE_SEPARATORS, '');
    return SA_VAT_REGEX.test(stripped) ? stripped : null;
}
/**
 * Zod schema for an optional VAT number on a react-hook-form form field.
 *
 * Accepts an empty string (the field is optional on every consumer) and
 * otherwise sanitises and validates, parsing to the canonical form so the
 * value the form submits is already clean. A single transform (rather than a
 * union) so the custom message surfaces directly as the field error. The
 * message is static so `fromZodError` never reflects user-supplied values.
 *
 * The strict wire schema `saVatNumberSchema` (api/common) shares the same
 * sanitiser; use this one only at the form boundary where blank is allowed.
 */
export const vatNumberFormFieldSchema = formFieldSanitisedText(sanitiseVatNumber, 'VAT number must be 10 digits starting with 4');
